import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { ThemeProvider as MuiThemeProvider, Theme } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { createAppTheme } from "@catalog/mui-theme";
import { setCookie, getCookie, areCookiesAvailable } from "@catalog/utils";

export type ThemeMode = "light" | "dark" | "system";

interface ThemeContextType {
  themeMode: ThemeMode;
  actualTheme: "light" | "dark";
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

const THEME_COOKIE_KEY = "catalog-app-theme-mode";
const LEGACY_THEME_STORAGE_KEY = "catalog-app-theme-mode"; // For localStorage migration

// Helper function to detect system theme preference
const getSystemTheme = (): "light" | "dark" => {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return "light";
};

// Helper function to migrate from localStorage to cookies
const migrateLegacyTheme = (): ThemeMode | null => {
  try {
    const legacyTheme = localStorage.getItem(LEGACY_THEME_STORAGE_KEY);
    if (legacyTheme && ["light", "dark", "system"].includes(legacyTheme)) {
      // Migrate to cookie
      storeThemeMode(legacyTheme as ThemeMode);
      // Remove from localStorage
      localStorage.removeItem(LEGACY_THEME_STORAGE_KEY);
      console.log(
        "🔄 Migrated theme preference from localStorage to cookies:",
        legacyTheme
      );
      return legacyTheme as ThemeMode;
    }
  } catch (error) {
    console.warn(
      "Failed to migrate theme preference from localStorage:",
      error
    );
  }
  return null;
};

// Helper function to get stored theme mode from cookies
const getStoredThemeMode = (): ThemeMode => {
  try {
    // First check if cookies are available
    if (!areCookiesAvailable()) {
      console.warn("Cookies not available, using default light theme");
      return "light";
    }

    // Try to get from cookies first
    const cookieTheme = getCookie(THEME_COOKIE_KEY);
    if (cookieTheme && ["light", "dark", "system"].includes(cookieTheme)) {
      console.log("✅ Theme preference loaded from cookies:", cookieTheme);
      return cookieTheme as ThemeMode;
    }

    // Try to migrate from localStorage if no cookie found
    const migratedTheme = migrateLegacyTheme();
    if (migratedTheme) {
      return migratedTheme;
    }
  } catch (error) {
    console.warn("Failed to read theme preference:", error);
  }

  // Default to light theme for first-time users
  console.log("🎨 Welcome! Setting default light theme for first-time user");
  const defaultTheme: ThemeMode = "light";
  // Save the default theme to cookies immediately
  storeThemeMode(defaultTheme);
  return defaultTheme;
};

// Helper function to store theme mode in cookies
const storeThemeMode = (mode: ThemeMode): void => {
  try {
    if (!areCookiesAvailable()) {
      console.warn("Cookies not available, cannot save theme preference");
      return;
    }

    // Set cookie to expire in 1 year
    setCookie(THEME_COOKIE_KEY, mode, {
      expires: 365, // 1 year in days
      path: "/", // Available across the entire site
      sameSite: "Lax", // Good balance of security and functionality
    });

    console.log("💾 Theme preference saved to cookies:", mode);
  } catch (error) {
    console.warn("Failed to save theme preference to cookies:", error);
  }
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() =>
    getStoredThemeMode()
  );
  const [systemTheme, setSystemTheme] = useState<"light" | "dark">(() =>
    getSystemTheme()
  );

  // Calculate the actual theme to use
  const actualTheme: "light" | "dark" =
    themeMode === "system" ? systemTheme : themeMode;
  const theme = createAppTheme(actualTheme);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? "dark" : "light");
    };

    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  // Update stored theme mode when it changes
  useEffect(() => {
    storeThemeMode(themeMode);
  }, [themeMode]);

  // Update document class for theme-specific styling
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("theme-light", "theme-dark");
    root.classList.add(`theme-${actualTheme}`);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        "content",
        actualTheme === "dark" ? "#0D1117" : "#F8F9FA"
      );
    }
  }, [actualTheme]);

  const setThemeMode = (mode: ThemeMode): void => {
    setThemeModeState(mode);
  };

  const toggleTheme = (): void => {
    if (themeMode === "system") {
      // If currently on system, toggle to the opposite of current system theme
      setThemeModeState(systemTheme === "dark" ? "light" : "dark");
    } else if (themeMode === "light") {
      setThemeModeState("dark");
    } else {
      setThemeModeState("light");
    }
  };

  const contextValue: ThemeContextType = {
    themeMode,
    actualTheme,
    toggleTheme,
    setThemeMode,
    theme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
